/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import {
  Controller,
  useForm,
  FieldValues,
  SubmitHandler
} from 'react-hook-form';
import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import installmentPlanService from 'service/installmentPlan';

interface InstallmentPlanAddProps {
  installmentData: any;
  setSelectedInstallmentId?: any;
  selectedInstallmentId?: number;
  setOpenInstallment: (open: boolean) => void;
  refetch: () => void;
}

const InstallmentPlanAdd = ({
  installmentData,
  selectedInstallmentId,
  setSelectedInstallmentId,
  setOpenInstallment,
  refetch
}: InstallmentPlanAddProps) => {
  const { t } = useTranslation();
  const { state, pathname } = useLocation();
  const { price } = state || {};
  const installmentPlanPid = pathname.split('/').pop();

  const dispatch: Dispatch<any> = useDispatch();
  const inputBarcodeRef = useRef<HTMLInputElement>(null);
  const hasFetchedData = useRef(false);

  const {
    control,
    watch,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const [loading, setLoading] = useState(false);

  const options = [
    {
      value: true,
      label: t('active')
    },
    {
      value: false,
      label: t('blocked')
    }
  ];

  const watchedPrice = Number(price);
  const month = Number(watch('month'));
  const percentage = Number(watch('percentage'));
  const quantity = Number(watch('quantity'));

  let total_price = 0;
  let monthly_price = 0;

  if (watchedPrice && month && percentage) {
    total_price = watchedPrice + (watchedPrice * percentage) / 100;

    if (quantity) {
      monthly_price = (total_price - quantity) / month;
    } else {
      monthly_price = total_price / month;
    }
  }

  //fetch with id
  const fetchData = () => {
    if (selectedInstallmentId === 0) return setLoading(false);

    installmentPlanService
      .getInstallmentPlanById(selectedInstallmentId)
      .then((res: any) => {
        const computed = {
          month: res?.month || 0,
          percentage: res?.additional_percentage || 0,
          quantity: res?.initial_payment || 0,
          status: res?.status ? res?.status : false
        };
        reset(computed);
      })
      .finally(() => setLoading(false));
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data: any) => {
    if (selectedInstallmentId !== 0) return updateData(data);

    createdData(data);
  };

  const createdData = async (data: any) => {
    setLoading(true);

    const month = +data.month;
    const percentage = +data.percentage;

    if (isNaN(month) || month <= 0 || isNaN(percentage) || percentage < 0) {
      dispatch(
        showAlert({
          title: t('invalid_month_or_percentage')
        })
      );
      setLoading(false);
      return;
    }

    // Check for duplicates in installmentData (skip for update if matching current ID)
    const isDuplicate = installmentData?.some(
      (item: any, index: any) =>
        (item.month === month || item.percentage === percentage) &&
        // Skip if updating the same item
        (selectedInstallmentId === 0 || index !== selectedInstallmentId)
    );

    if (isDuplicate) {
      dispatch(
        showAlert({
          title: t('duplicate_month_or_percentage')
        })
      );
      setLoading(false);
      return;
    }

    if (monthly_price < 0) {
      dispatch(
        showAlert({
          title: `${t('monthly_price')}.${t('quantity_must_not_less_zero')}`
        })
      );
      setLoading(false);
      return;
    }

    const createdData = {
      additional_percentage: +data.percentage,
      final_amount: +total_price,
      initial_payment: +data.quantity || 0,
      installment_product_pid: Number(installmentPlanPid || 0),
      month: +data.month,
      monthly_amount: +monthly_price,
      status: true
    };

    try {
      await installmentPlanService
        .createInstallment(createdData)
        .then((res: any) => {
          if (res) {
            dispatch(
              showAlert({
                type: 'success',
                title: t('successfully_added')
              })
            );
            setLoading(false);
            setOpenInstallment(false);
            refetch();
          }
        });
    } catch (error: any) {
      console.log(error);
      setLoading(false);
    }
  };

  const updateData = (data: any) => {
    setLoading(true);

    if (monthly_price < 0) {
      dispatch(
        showAlert({
          title: `${t('monthly_price')}.${t('quantity_must_not_less_zero')}`
        })
      );
      setLoading(false);
      return;
    }
    const updatedData = {
      additional_percentage: +data.percentage,
      final_amount: +total_price,
      initial_payment: +data.quantity || 0,
      pid: selectedInstallmentId,
      // installment_product_pid: Number(installmentPlanPid || 0),
      month: +data.month,
      monthly_amount: +monthly_price,
      // prettier-ignore
      status:
        data?.status === 'true' || data?.status === true
        ? true
          : data?.status === 'false' || data?.status === false
            ? false
            : false
    };

    installmentPlanService
      .updateInstallment(selectedInstallmentId, updatedData)
      .then((res: any) => {
        if (res) {
          dispatch(
            showAlert({
              title: `${t('products')} ${t('successfully_added')}`,
              type: 'success'
            })
          );
          refetch();
          setLoading(false);
          setOpenInstallment(false);
          setSelectedInstallmentId(0);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (inputBarcodeRef.current) {
      inputBarcodeRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (selectedInstallmentId !== 0 && !hasFetchedData.current) {
      fetchData();
      hasFetchedData.current = true;
    }
  }, [selectedInstallmentId]);

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Row className="mb-2">
          <Col>
            <Controller
              name="month"
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Form.Floating>
                  <Form.Control
                    {...field}
                    disabled={selectedInstallmentId !== 0 ? true : false}
                    ref={inputBarcodeRef}
                    type="number"
                    placeholder={t('month')}
                    onFocus={e => {
                      e.target.select();
                    }}
                    autoComplete="off"
                    isInvalid={!!errors.month}
                  />
                  <label htmlFor="floatingInputCustom">{t('month')}</label>
                  {errors.month && (
                    <Form.Control.Feedback type="invalid">
                      {t('required_field')}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              )}
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Controller
              name="percentage"
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Form.Floating>
                  <Form.Control
                    {...field}
                    type="number"
                    placeholder={t('percentage')}
                    onFocus={e => {
                      e.target.select();
                    }}
                    autoComplete="off"
                    isInvalid={!!errors.percentage}
                  />
                  <label htmlFor="floatingInputCustom">{t('percentage')}</label>
                  {errors.percentage && (
                    <Form.Control.Feedback type="invalid">
                      {t('required_field')}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              )}
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Controller
              name="quantity"
              control={control}
              render={({ field }) => (
                <Form.Floating>
                  <Form.Control
                    {...field}
                    type="number"
                    placeholder={t('quantity_repayment')}
                    onFocus={e => {
                      e.target.select();
                    }}
                    autoComplete="off"
                  />
                  <label htmlFor="floatingInputCustom">
                    {t('quantity_repayment')}
                  </label>
                </Form.Floating>
              )}
            />
          </Col>
        </Row>

        {selectedInstallmentId !== 0 && (
          <Row className="mb-3">
            <Col>
              <div className="react-select-container">
                <Controller
                  name="status"
                  rules={{
                    required: true
                  }}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select {...field} isInvalid={!!errors.status}>
                        <option className="d-none" value=""></option>
                        {options?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">{t('status')}</label>
                      {errors.status && (
                        <Form.Control.Feedback type="invalid">
                          {t('required_field')}
                        </Form.Control.Feedback>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Col>
          </Row>
        )}

        {total_price > 0 && month > 0 && (
          <Row className="mb-2">
            <Col>
              <div className="d-flex gap-2">
                <strong>{t('total_price')}:</strong>
                {total_price
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(total_price)
                      .replace(/,/g, ' ')
                  : 0}
              </div>
              <div className="d-flex gap-2">
                <strong>{t('monthly_price')}:</strong>{' '}
                {monthly_price
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(monthly_price)
                      .replace(/,/g, ' ')
                  : 0}
              </div>
            </Col>
          </Row>
        )}

        <Button
          style={{ height: '48px', marginTop: 'auto' }}
          variant="primary"
          type="submit"
          loading={loading}
          loadingPosition="start"
        >
          {t('add')}
        </Button>
      </Form>
    </>
  );
};

export default InstallmentPlanAdd;
